import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  PlayIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  QueueListIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import AuthenticatedLayout from '../components/AuthenticatedLayout';
import AnalysisRuleViolationsPanel from '../components/policy/AnalysisRuleViolationsPanel';
import analysisService from '../services/analysis.service';
import { formatDateTime, getTimezoneAbbreviation } from '../utils/dateFormat';
import {
  AnalysisDetail as AnalysisDetailType,
  AnalysisStatus,
} from '../types/analysis.types';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

// Known coaching category base keys for fuzzy matching
const COACHING_CATEGORY_KEYS = [
  'greeting', 'identity', 'disclosure', 'purpose', 'empathy',
  'objections', 'resolution', 'language', 'interruptions', 'structure', 'general'
];

/**
 * Get translated sensitive data type.
 * Falls back to the raw key if translation not found.
 */
function getSensitiveDataTypeTranslation(
  type: string,
  t: ReturnType<typeof useTranslation>['t']
): string {
  const translationKey = `analysisDetail:sensitiveDataTypes.${type}`;
  const translated = t(translationKey, { defaultValue: '' });
  // If no translation found, format the key nicely
  return translated || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Get translated call stage name.
 * Falls back to the raw key if translation not found.
 */
function getCallStageTranslation(
  stage: string,
  t: ReturnType<typeof useTranslation>['t']
): string {
  const translationKey = `analysisDetail:callStages.${stage}`;
  const translated = t(translationKey, { defaultValue: '' });
  return translated || stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Get translated persuasion technique name.
 * Falls back to the raw key if translation not found.
 */
function getPersuasionTechniqueTranslation(
  technique: string,
  t: ReturnType<typeof useTranslation>['t']
): string {
  const translationKey = `analysisDetail:persuasionTechniques.${technique}`;
  const translated = t(translationKey, { defaultValue: '' });
  return translated || technique.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Get translated language issue type.
 * Falls back to the raw key if translation not found.
 */
function getLanguageIssueTranslation(
  issueType: string,
  t: ReturnType<typeof useTranslation>['t']
): string {
  const translationKey = `analysisDetail:languageIssues.${issueType}`;
  const translated = t(translationKey, { defaultValue: '' });
  return translated || issueType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Find the best matching translation key for a coaching category.
 * AI may generate variations like "greeting timing" or "resolution confirmation".
 * This function tries exact match first, then partial match, then returns the original.
 */
function findCategoryTranslationKey(category: string): string {
  const normalized = category.toLowerCase();

  // Try exact match first (handles "greeting timing", "call structure", etc.)
  // The translation file may have exact matches for common variations

  // Then try to find a base category that the input contains
  for (const baseKey of COACHING_CATEGORY_KEYS) {
    if (normalized.includes(baseKey)) {
      return baseKey;
    }
  }

  // Fall back to the original (will use exact match or original text)
  return normalized;
}

/**
 * Get recommendation text for a coaching recommendation.
 * Supports both v1 (description) and v2 (recommendation_key) formats.
 *
 * @param rec - The coaching recommendation
 * @param t - Translation function from i18next
 * @returns Object with title and description for display
 */
function getRecommendationText(
  rec: { category: string; description?: string; recommendation_key?: string },
  t: ReturnType<typeof useTranslation>['t']
): { title: string; description: string } {
  // V2 format: Use recommendation_key for i18n lookup
  if (rec.recommendation_key) {
    const titleKey = `analysisDetail:coaching.recommendationKeys.${rec.recommendation_key}.title`;
    const descKey = `analysisDetail:coaching.recommendationKeys.${rec.recommendation_key}.description`;
    const categoryFallback = t(`analysisDetail:coaching.categories.${findCategoryTranslationKey(rec.category)}`, rec.category);

    // Try to get the title from recommendation key, fall back to category
    const keyTitle = t(titleKey, { defaultValue: categoryFallback as string });
    const keyDescription = t(descKey, { defaultValue: rec.description || '' });

    return { title: keyTitle as string, description: keyDescription as string };
  }

  // V1 fallback: Use legacy description
  const categoryKey = `analysisDetail:coaching.categories.${rec.category.toLowerCase()}`;
  const categoryFallbackKey = `analysisDetail:coaching.categories.${findCategoryTranslationKey(rec.category)}`;
  const categoryTitle = t(categoryKey, { defaultValue: t(categoryFallbackKey, rec.category) as string });

  return { title: categoryTitle as string, description: rec.description || '' };
}

/**
 * Get coaching item text for the three-tier model.
 * Uses recommendation_key for i18n lookup.
 *
 * @param item - The coaching item (issues, strengths, or growth_opportunities)
 * @param t - Translation function from i18next
 * @returns Object with title and description for display
 */
function getCoachingItemText(
  item: { category: string; recommendation_key: string; timestamp?: number | null; context?: string | null },
  t: ReturnType<typeof useTranslation>['t']
): { title: string; description: string } {
  const titleKey = `analysisDetail:coaching.recommendationKeys.${item.recommendation_key}.title`;
  const descKey = `analysisDetail:coaching.recommendationKeys.${item.recommendation_key}.description`;
  const categoryFallback = t(`analysisDetail:coaching.categories.${findCategoryTranslationKey(item.category)}`, item.category);

  // Try to get the title from recommendation key, fall back to category
  const title = t(titleKey, { defaultValue: categoryFallback as string });
  let description = t(descKey, { defaultValue: '' });

  // Append context if provided (e.g., benchmark comparison)
  if (item.context) {
    description = description ? `${description} ${item.context}` : item.context;
  }

  return { title: title as string, description: description as string };
}

// Types for violations and issues
interface ComplianceViolation {
  key: string;
  title: string;
  description: string;
  details?: string;
  timestamp?: number;
}

interface QualityIssue {
  key: string;
  title: string;
  description: string;
  details?: string;
}

interface QualityStrength {
  key: string;
  title: string;
}

const AnalysisDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('analysisDetail');
  const [analysis, setAnalysis] = useState<AnalysisDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const analysisData = await analysisService.getAnalysis(id!);
      setAnalysis(analysisData);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/signin');
      } else {
        setError(error.response?.data?.detail || t('analysisDetail:header.errorLoading'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return t('analysisDetail:overview.na');
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate compliance score (passed / total)
  const getComplianceScore = () => {
    if (!analysis?.results?.compliance) return { passed: 0, total: 5 };
    const c = analysis.results.compliance;
    let passed = 0;

    if (!c.identity_verification.flagged) passed++;
    if (c.mandatory_disclosures.read) passed++;
    if (!c.purpose_declaration.flagged) passed++;
    if (!c.prohibited_phrases.used) passed++;
    if (!c.sensitive_data.shared) passed++;

    return { passed, total: 5 };
  };

  // Get compliance violations with details
  const getComplianceViolations = (): ComplianceViolation[] => {
    if (!analysis?.results?.compliance) return [];
    const violations: ComplianceViolation[] = [];
    const c = analysis.results.compliance;

    if (c.identity_verification.flagged) {
      violations.push({
        key: 'identity',
        title: t('analysisDetail:compliance.identityVerification.title'),
        description: t('analysisDetail:compliance.identityVerification.notVerified'),
        timestamp: c.identity_verification.timestamp || undefined,
      });
    }
    if (!c.mandatory_disclosures.read && c.mandatory_disclosures.missing_disclosures.length > 0) {
      violations.push({
        key: 'disclosures',
        title: t('analysisDetail:compliance.mandatoryDisclosures.title'),
        description: t('analysisDetail:compliance.mandatoryDisclosures.notRead'),
        details: `${c.mandatory_disclosures.missing_disclosures.length} ${t('analysisDetail:compliance.mandatoryDisclosures.missingCount')}`,
      });
    }
    if (c.purpose_declaration.flagged) {
      violations.push({
        key: 'purpose',
        title: t('analysisDetail:compliance.purposeDeclaration.title'),
        description: t('analysisDetail:compliance.purposeDeclaration.notDeclared'),
      });
    }
    if (c.prohibited_phrases.used) {
      const instances = c.prohibited_phrases.instances;
      violations.push({
        key: 'prohibited',
        title: t('analysisDetail:compliance.prohibitedPhrases.title'),
        description: t('analysisDetail:compliance.prohibitedPhrases.detected'),
        details: instances.length > 0
          ? `"${instances[0].phrase}" ${t('analysisDetail:compliance.prohibitedPhrases.usedAt')} ${formatTimestamp(instances[0].timestamp)}${instances.length > 1 ? ` +${instances.length - 1} ${t('analysisDetail:compliance.prohibitedPhrases.more')}` : ''}`
          : undefined,
        timestamp: instances.length > 0 ? instances[0].timestamp : undefined,
      });
    }
    if (c.sensitive_data.shared) {
      const instances = c.sensitive_data.instances;
      violations.push({
        key: 'sensitive',
        title: t('analysisDetail:compliance.sensitiveData.title'),
        description: t('analysisDetail:compliance.sensitiveData.exposureDetected'),
        details: instances.length > 0
          ? `${getSensitiveDataTypeTranslation(instances[0].type, t)} ${t('analysisDetail:compliance.sensitiveData.detectedAt')} ${formatTimestamp(instances[0].timestamp)}`
          : undefined,
        timestamp: instances.length > 0 ? instances[0].timestamp : undefined,
      });
    }

    return violations;
  };

  // Get passed compliance checks
  const getPassedComplianceChecks = (): string[] => {
    if (!analysis?.results?.compliance) return [];
    const passed: string[] = [];
    const c = analysis.results.compliance;

    if (!c.identity_verification.flagged) passed.push(t('analysisDetail:compliance.identityVerification.title'));
    if (c.mandatory_disclosures.read) passed.push(t('analysisDetail:compliance.mandatoryDisclosures.title'));
    if (!c.purpose_declaration.flagged) passed.push(t('analysisDetail:compliance.purposeDeclaration.title'));
    if (!c.prohibited_phrases.used) passed.push(t('analysisDetail:compliance.prohibitedPhrases.title'));
    if (!c.sensitive_data.shared) passed.push(t('analysisDetail:compliance.sensitiveData.title'));

    return passed;
  };

  // Calculate quality score (passed / total)
  const getQualityScore = () => {
    if (!analysis?.results?.quality) return { passed: 0, total: 9 };
    const q = analysis.results.quality;
    let passed = 0;

    if (!q.sentiment_analysis.flagged_drop) passed++;
    if (!q.greeting.flagged_missing && !q.greeting.flagged_late) passed++;
    if (q.call_structure.follows_structure) passed++;
    if (q.resolution_marker.confirmed) passed++;
    if (!q.empathetic_language.flagged) passed++;
    if (!q.objections.flagged_unhandled) passed++;
    // Persuasion markers are always informational, not pass/fail
    passed++; // Count as passed since it's informational
    if (!q.interruptions.flagged) passed++;
    if (!q.language_adequacy.flagged) passed++;

    return { passed, total: 9 };
  };

  // Get quality issues (opportunities for improvement)
  const getQualityIssues = (): QualityIssue[] => {
    if (!analysis?.results?.quality) return [];
    const issues: QualityIssue[] = [];
    const q = analysis.results.quality;

    if (q.sentiment_analysis.flagged_drop) {
      issues.push({
        key: 'sentiment',
        title: t('analysisDetail:quality.sentimentAnalysis.title'),
        description: t('analysisDetail:quality.sentimentAnalysis.significantDrop'),
        details: `${t('analysisDetail:quality.sentimentAnalysis.dropOf')} ${q.sentiment_analysis.drop_magnitude} ${t('analysisDetail:quality.sentimentAnalysis.levels')}`,
      });
    }
    if (q.greeting.flagged_missing || q.greeting.flagged_late) {
      issues.push({
        key: 'greeting',
        title: t('analysisDetail:quality.greeting.title'),
        description: t('analysisDetail:quality.greeting.missing'),
      });
    }
    if (!q.call_structure.follows_structure) {
      issues.push({
        key: 'structure',
        title: t('analysisDetail:quality.callStructure.title'),
        description: t('analysisDetail:quality.callStructure.incomplete'),
        details: `${q.call_structure.missing_stages.length} ${t('analysisDetail:quality.callStructure.missingStages')}`,
      });
    }
    if (!q.resolution_marker.confirmed) {
      issues.push({
        key: 'resolution',
        title: t('analysisDetail:quality.resolutionMarker.title'),
        description: t('analysisDetail:quality.resolutionMarker.notConfirmedDesc'),
      });
    }
    if (q.empathetic_language.flagged) {
      issues.push({
        key: 'empathy',
        title: t('analysisDetail:quality.empatheticLanguage.title'),
        description: t('analysisDetail:quality.empatheticLanguage.lacking'),
        details: `${q.empathetic_language.intervals_without_empathy} ${t('analysisDetail:quality.empatheticLanguage.intervalsWithout')}`,
      });
    }
    if (q.objections.flagged_unhandled) {
      const unhandled = q.objections.identified.filter(o => !o.handled).length;
      issues.push({
        key: 'objections',
        title: t('analysisDetail:quality.objections.title'),
        description: t('analysisDetail:quality.objections.unhandledDesc'),
        details: `${unhandled} ${t('analysisDetail:quality.objections.objectionsUnhandled')}`,
      });
    }
    if (q.interruptions.flagged) {
      issues.push({
        key: 'interruptions',
        title: t('analysisDetail:quality.interruptions.title'),
        description: t('analysisDetail:quality.interruptions.tooMany'),
        details: `${q.interruptions.count} ${t('analysisDetail:quality.interruptions.detected')}`,
      });
    }
    if (q.language_adequacy.flagged) {
      issues.push({
        key: 'language',
        title: t('analysisDetail:quality.languageAdequacy.title'),
        description: t('analysisDetail:quality.languageAdequacy.issuesDetected'),
        details: `${q.language_adequacy.issues.length} ${t('analysisDetail:quality.languageAdequacy.issues')}`,
      });
    }

    return issues;
  };

  // Get quality strengths
  const getQualityStrengths = (): QualityStrength[] => {
    if (!analysis?.results?.quality) return [];
    const strengths: QualityStrength[] = [];
    const q = analysis.results.quality;

    if (!q.sentiment_analysis.flagged_drop) {
      strengths.push({ key: 'sentiment', title: t('analysisDetail:quality.sentimentAnalysis.title') });
    }
    if (!q.greeting.flagged_missing && !q.greeting.flagged_late) {
      strengths.push({ key: 'greeting', title: t('analysisDetail:quality.greeting.title') });
    }
    if (q.call_structure.follows_structure) {
      strengths.push({ key: 'structure', title: t('analysisDetail:quality.callStructure.title') });
    }
    if (q.resolution_marker.confirmed) {
      strengths.push({ key: 'resolution', title: t('analysisDetail:quality.resolutionMarker.title') });
    }
    if (!q.empathetic_language.flagged) {
      strengths.push({ key: 'empathy', title: t('analysisDetail:quality.empatheticLanguage.title') });
    }
    if (!q.objections.flagged_unhandled) {
      strengths.push({ key: 'objections', title: t('analysisDetail:quality.objections.title') });
    }
    if (!q.interruptions.flagged) {
      strengths.push({ key: 'interruptions', title: t('analysisDetail:quality.interruptions.title') });
    }
    if (!q.language_adequacy.flagged) {
      strengths.push({ key: 'language', title: t('analysisDetail:quality.languageAdequacy.title') });
    }
    // Add persuasion markers as strength if used
    if (q.persuasion_markers.count > 0) {
      strengths.push({ key: 'persuasion', title: `${q.persuasion_markers.count} ${t('analysisDetail:quality.persuasionMarkers.used')}` });
    }

    return strengths;
  };

  // Get effort score interpretation
  const getEffortInterpretation = (score: number) => {
    if (score <= 2) return { label: t('analysisDetail:hero.lowEffort'), color: 'green' };
    if (score <= 3) return { label: t('analysisDetail:hero.moderateEffort'), color: 'amber' };
    return { label: t('analysisDetail:hero.highEffort'), color: 'red' };
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error || !analysis) {
    return (
      <AuthenticatedLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow sm:rounded-lg p-6">
            <div className="text-center">
              <XCircleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {t('analysisDetail:header.errorLoading')}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {error || t('analysisDetail:header.analysisNotFound')}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/analyses')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5" />
                  {t('analysisDetail:header.backButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  const complianceScore = getComplianceScore();
  const complianceViolations = getComplianceViolations();
  const passedComplianceChecks = getPassedComplianceChecks();
  const qualityScore = getQualityScore();
  const qualityIssues = getQualityIssues();
  const qualityStrengths = getQualityStrengths();
  const hasResults = analysis.status === AnalysisStatus.COMPLETED && analysis.results;
  const effortScore = analysis.results?.coaching?.customer_effort_score || 0;
  const effortInterpretation = getEffortInterpretation(effortScore);

  return (
    <AuthenticatedLayout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/analyses')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            {t('analysisDetail:header.backToAnalyses')}
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('analysisDetail:header.title')}</h1>
              <p className="mt-1 text-sm text-gray-500">
                {t('analysisDetail:header.idPrefix')} {analysis.id} • {formatDateTime(analysis.created, i18n.language)} ({getTimezoneAbbreviation()})
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {analysis.status === AnalysisStatus.QUEUED && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  <QueueListIcon className="h-4 w-4 mr-1" />
                  {t('analysisDetail:status.queued')}
                </span>
              )}
              {analysis.status === AnalysisStatus.COMPLETED && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  {t('analysisDetail:status.completed')}
                </span>
              )}
              {analysis.status === AnalysisStatus.PROCESSING && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <ClockIcon className="h-4 w-4 mr-1 animate-spin" />
                  {t('analysisDetail:status.processing')}
                </span>
              )}
              {analysis.status === AnalysisStatus.FAILED && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  <XCircleIcon className="h-4 w-4 mr-1" />
                  {t('analysisDetail:status.failed')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* State Messages */}
        {analysis.status === AnalysisStatus.QUEUED && (
          <div className="bg-gray-50 border-l-4 border-gray-400 p-4 mb-6 rounded-r-lg">
            <div className="flex">
              <QueueListIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <p className="ml-3 text-sm text-gray-700">{t('analysisDetail:messages.queuedNotice')}</p>
            </div>
          </div>
        )}
        {analysis.status === AnalysisStatus.PROCESSING && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r-lg">
            <div className="flex">
              <ClockIcon className="h-5 w-5 text-blue-400 flex-shrink-0 animate-spin" />
              <p className="ml-3 text-sm text-blue-700">{t('analysisDetail:messages.processingNotice')}</p>
            </div>
          </div>
        )}
        {analysis.status === AnalysisStatus.FAILED && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-r-lg">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
              <p className="ml-3 text-sm text-red-700">
                {t('analysisDetail:messages.failedNotice')} {analysis.metadata.error_message || t('analysisDetail:messages.unknownError')}
              </p>
            </div>
          </div>
        )}

        {/* Rule Violations Panel — categorized rules triggered on this call */}
        {hasResults && analysis.id && (
          <div className="mb-6">
            <AnalysisRuleViolationsPanel analysisId={analysis.id} />
          </div>
        )}

        {/* Hero Section - Score Summary Cards */}
        {hasResults && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {/* Compliance Score Card */}
            <div className={classNames(
              'rounded-xl p-5 border-2 transition-all',
              complianceViolations.length > 0
                ? 'bg-red-50 border-red-200'
                : 'bg-green-50 border-green-200'
            )}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className={classNames(
                    'h-5 w-5',
                    complianceViolations.length > 0 ? 'text-red-600' : 'text-green-600'
                  )} />
                  <span className="text-sm font-medium text-gray-700">
                    {t('analysisDetail:hero.complianceScore')}
                  </span>
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={classNames(
                  'text-3xl font-bold',
                  complianceViolations.length > 0 ? 'text-red-700' : 'text-green-700'
                )}>
                  {complianceScore.passed}/{complianceScore.total}
                </span>
                <span className={classNames(
                  'text-sm font-medium px-2 py-0.5 rounded-full',
                  complianceViolations.length > 0
                    ? 'bg-red-100 text-red-700'
                    : 'bg-green-100 text-green-700'
                )}>
                  {complianceViolations.length > 0
                    ? `${complianceViolations.length} ${complianceViolations.length === 1 ? t('analysisDetail:hero.violation') : t('analysisDetail:hero.violations')}`
                    : t('analysisDetail:hero.passedAll')
                  }
                </span>
              </div>
            </div>

            {/* Quality Score Card */}
            <div className={classNames(
              'rounded-xl p-5 border-2 transition-all',
              qualityIssues.length > 0
                ? 'bg-amber-50 border-amber-200'
                : 'bg-green-50 border-green-200'
            )}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SparklesIcon className={classNames(
                    'h-5 w-5',
                    qualityIssues.length > 0 ? 'text-amber-600' : 'text-green-600'
                  )} />
                  <span className="text-sm font-medium text-gray-700">
                    {t('analysisDetail:hero.qualityScore')}
                  </span>
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={classNames(
                  'text-3xl font-bold',
                  qualityIssues.length > 0 ? 'text-amber-700' : 'text-green-700'
                )}>
                  {qualityScore.passed}/{qualityScore.total}
                </span>
                <span className={classNames(
                  'text-sm font-medium px-2 py-0.5 rounded-full',
                  qualityIssues.length > 0
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-green-100 text-green-700'
                )}>
                  {qualityIssues.length > 0
                    ? `${qualityIssues.length} ${qualityIssues.length === 1 ? t('analysisDetail:hero.issue') : t('analysisDetail:hero.issues')}`
                    : t('analysisDetail:hero.passedAll')
                  }
                </span>
              </div>
            </div>

            {/* Customer Effort Score Card */}
            <div className={classNames(
              'rounded-xl p-5 border-2 transition-all',
              effortInterpretation.color === 'red'
                ? 'bg-red-50 border-red-200'
                : effortInterpretation.color === 'amber'
                ? 'bg-amber-50 border-amber-200'
                : 'bg-green-50 border-green-200'
            )}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <UserIcon className={classNames(
                    'h-5 w-5',
                    effortInterpretation.color === 'red'
                      ? 'text-red-600'
                      : effortInterpretation.color === 'amber'
                      ? 'text-amber-600'
                      : 'text-green-600'
                  )} />
                  <span className="text-sm font-medium text-gray-700">
                    {t('analysisDetail:hero.effortScore')}
                  </span>
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={classNames(
                  'text-3xl font-bold',
                  effortInterpretation.color === 'red'
                    ? 'text-red-700'
                    : effortInterpretation.color === 'amber'
                    ? 'text-amber-700'
                    : 'text-green-700'
                )}>
                  {effortScore}/5
                </span>
                <span className={classNames(
                  'text-sm font-medium px-2 py-0.5 rounded-full',
                  effortInterpretation.color === 'red'
                    ? 'bg-red-100 text-red-700'
                    : effortInterpretation.color === 'amber'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-green-100 text-green-700'
                )}>
                  {effortInterpretation.label}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Monitoring Section */}
        {hasResults && (
          <div className="bg-white shadow rounded-xl mb-6 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShieldCheckIcon className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('analysisDetail:sections.complianceMonitoring')}
                </h2>
              </div>
              <span className={classNames(
                'text-sm font-medium px-3 py-1 rounded-full',
                complianceViolations.length > 0
                  ? 'bg-red-100 text-red-700'
                  : 'bg-green-100 text-green-700'
              )}>
                {complianceScore.passed}/{complianceScore.total} {t('analysisDetail:hero.passed')}
              </span>
            </div>

            <div className="p-6">
              {/* Violations Section */}
              {complianceViolations.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-red-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4" />
                    {t('analysisDetail:complianceSection.requiresAttention')}
                  </h3>
                  <div className="space-y-3">
                    {complianceViolations.map((violation) => (
                      <div
                        key={violation.key}
                        className="bg-red-50 border border-red-200 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3">
                          <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-red-800">{violation.title}</p>
                            <p className="text-sm text-red-700 mt-0.5">{violation.description}</p>
                            {violation.details && (
                              <p className="text-xs text-red-600 mt-1 font-medium">{violation.details}</p>
                            )}
                          </div>
                          <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">
                            {t('analysisDetail:complianceSection.reviewRecommended')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Passed Checks Section */}
              {passedComplianceChecks.length > 0 && (
                <div>
                  {complianceViolations.length > 0 && (
                    <h3 className="text-sm font-semibold text-green-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4" />
                      {t('analysisDetail:complianceSection.passedChecks')}
                    </h3>
                  )}
                  <div className={classNames(
                    'flex flex-wrap gap-2',
                    complianceViolations.length === 0 ? 'bg-green-50 border border-green-200 rounded-lg p-4' : ''
                  )}>
                    {complianceViolations.length === 0 && (
                      <div className="w-full flex items-center gap-2 mb-2">
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-green-700">
                          {t('analysisDetail:complianceSection.allChecksPassed')}
                        </span>
                      </div>
                    )}
                    {passedComplianceChecks.map((check, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full"
                      >
                        <CheckCircleIcon className="h-3 w-3" />
                        {check}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quality Insights Section */}
        {hasResults && (
          <div className="bg-white shadow rounded-xl mb-6 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SparklesIcon className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('analysisDetail:sections.qualityInsights')}
                </h2>
              </div>
              <span className={classNames(
                'text-sm font-medium px-3 py-1 rounded-full',
                qualityIssues.length > 0
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-green-100 text-green-700'
              )}>
                {qualityScore.passed}/{qualityScore.total} {t('analysisDetail:hero.passed')}
              </span>
            </div>

            <div className="p-6">
              {/* Sentiment Journey Visualization */}
              {analysis.results?.quality?.sentiment_analysis && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    {t('analysisDetail:qualitySection.sentimentJourney')}
                  </h4>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{t('analysisDetail:qualitySection.startedAt')}</span>
                      <span className={classNames(
                        'text-sm font-bold',
                        analysis.results.quality.sentiment_analysis.first_30s_avg >= 4 ? 'text-green-600' :
                        analysis.results.quality.sentiment_analysis.first_30s_avg >= 3 ? 'text-amber-600' :
                        'text-red-600'
                      )}>
                        {analysis.results.quality.sentiment_analysis.first_30s_avg}/5
                      </span>
                    </div>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={classNames(
                          'h-full rounded-full transition-all',
                          analysis.results.quality.sentiment_analysis.last_30s_avg >= 4
                            ? 'bg-gradient-to-r from-amber-400 via-green-400 to-green-500'
                            : analysis.results.quality.sentiment_analysis.last_30s_avg >= 3
                            ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                            : 'bg-gradient-to-r from-red-400 to-amber-400'
                        )}
                        style={{ width: `${(analysis.results.quality.sentiment_analysis.last_30s_avg / 5) * 100}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{t('analysisDetail:qualitySection.endedAt')}</span>
                      <span className={classNames(
                        'text-sm font-bold',
                        analysis.results.quality.sentiment_analysis.last_30s_avg >= 4 ? 'text-green-600' :
                        analysis.results.quality.sentiment_analysis.last_30s_avg >= 3 ? 'text-amber-600' :
                        'text-red-600'
                      )}>
                        {analysis.results.quality.sentiment_analysis.last_30s_avg}/5
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Issues and Strengths Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Opportunities Column */}
                <div>
                  <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    {t('analysisDetail:qualitySection.opportunities')}
                  </h3>
                  {qualityIssues.length > 0 ? (
                    <div className="space-y-2">
                      {qualityIssues.map((issue) => (
                        <div
                          key={issue.key}
                          className="bg-amber-50 border border-amber-200 rounded-lg p-3"
                        >
                          <p className="text-sm font-medium text-amber-800">{issue.title}</p>
                          <p className="text-xs text-amber-700 mt-0.5">{issue.description}</p>
                          {issue.details && (
                            <p className="text-xs text-amber-600 mt-1">{issue.details}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                      <CheckCircleIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">{t('analysisDetail:qualitySection.noOpportunitiesIdentified')}</p>
                    </div>
                  )}
                </div>

                {/* Strengths Column */}
                <div>
                  <h3 className="text-sm font-semibold text-green-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4" />
                    {t('analysisDetail:qualitySection.strengths')}
                  </h3>
                  {qualityStrengths.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {qualityStrengths.map((strength) => (
                        <span
                          key={strength.key}
                          className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2.5 py-1.5 rounded-full"
                        >
                          <CheckCircleIcon className="h-3 w-3" />
                          {strength.title}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-600">{t('analysisDetail:qualitySection.noStrengthsIdentified')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Coaching Insights Section */}
        {hasResults && analysis.results?.coaching && (
          <div className="bg-white shadow rounded-xl mb-6 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('analysisDetail:sections.coachingInsights')}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Three-tier coaching model */}
              {(analysis.results.coaching.issues?.length ||
                analysis.results.coaching.strengths?.length ||
                analysis.results.coaching.growth_opportunities?.length) ? (
                <>
                  {/* Issues Section (RED) - Critical issues from failed checks */}
                  {analysis.results.coaching.issues && analysis.results.coaching.issues.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                        <XCircleIcon className="h-5 w-5" />
                        {t('analysisDetail:coaching.tiers.issues.title')}
                      </h3>
                      <div className="space-y-2">
                        {analysis.results.coaching.issues.map((item, idx) => {
                          const { title, description } = getCoachingItemText(item, t);
                          return (
                            <div
                              key={`issue-${idx}`}
                              className="p-4 rounded-lg border bg-red-50 border-red-200"
                            >
                              <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 text-xs px-2 py-1 rounded font-bold text-white bg-red-500">
                                  {t('analysisDetail:coaching.tiers.issues.badge')}
                                </span>
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{title}</p>
                                  <p className="text-xs text-gray-600 mt-1">{description}</p>
                                  {item.timestamp != null && (
                                    <p className="text-xs text-red-600 mt-1">
                                      {t('analysisDetail:coaching.tiers.atTimestamp', { time: Math.round(item.timestamp) })}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Strengths Section (GREEN) - Positive behaviors to reinforce */}
                  {analysis.results.coaching.strengths && analysis.results.coaching.strengths.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5" />
                        {t('analysisDetail:coaching.tiers.strengths.title')}
                      </h3>
                      <div className="space-y-2">
                        {analysis.results.coaching.strengths.map((item, idx) => {
                          const { title, description } = getCoachingItemText(item, t);
                          return (
                            <div
                              key={`strength-${idx}`}
                              className="p-4 rounded-lg border bg-green-50 border-green-200"
                            >
                              <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 text-xs px-2 py-1 rounded font-bold text-white bg-green-500">
                                  {t('analysisDetail:coaching.tiers.strengths.badge')}
                                </span>
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{title}</p>
                                  <p className="text-xs text-gray-600 mt-1">{description}</p>
                                  {item.timestamp != null && (
                                    <p className="text-xs text-green-600 mt-1">
                                      {t('analysisDetail:coaching.tiers.atTimestamp', { time: Math.round(item.timestamp) })}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Growth Opportunities Section (YELLOW) - Aspirational improvements */}
                  {analysis.results.coaching.growth_opportunities && analysis.results.coaching.growth_opportunities.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                        <SparklesIcon className="h-5 w-5" />
                        {t('analysisDetail:coaching.tiers.growthOpportunities.title')}
                      </h3>
                      <div className="space-y-2">
                        {analysis.results.coaching.growth_opportunities.map((item, idx) => {
                          const { title, description } = getCoachingItemText(item, t);
                          return (
                            <div
                              key={`growth-${idx}`}
                              className="p-4 rounded-lg border bg-amber-50 border-amber-200"
                            >
                              <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 text-xs px-2 py-1 rounded font-bold text-white bg-amber-500">
                                  {t('analysisDetail:coaching.tiers.growthOpportunities.badge')}
                                </span>
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{title}</p>
                                  <p className="text-xs text-gray-600 mt-1">{description}</p>
                                  {item.timestamp != null && (
                                    <p className="text-xs text-amber-600 mt-1">
                                      {t('analysisDetail:coaching.tiers.atTimestamp', { time: Math.round(item.timestamp) })}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Show message if all three tiers are empty */}
                  {!analysis.results.coaching.issues?.length &&
                   !analysis.results.coaching.strengths?.length &&
                   !analysis.results.coaching.growth_opportunities?.length && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700">{t('analysisDetail:coaching.tiers.noCoaching')}</p>
                    </div>
                  )}
                </>
              ) : (
                /* Legacy: Priority-based Recommendation Cards */
                <div className="space-y-3">
                  {analysis.results.coaching.recommendations
                    ?.sort((a, b) => {
                      const priorityOrder = { high: 0, medium: 1, low: 2 };
                      return priorityOrder[a.priority as keyof typeof priorityOrder] - priorityOrder[b.priority as keyof typeof priorityOrder];
                    })
                    .map((rec, idx) => {
                      const { title, description } = getRecommendationText(rec, t);
                      return (
                        <div
                          key={idx}
                          className={classNames(
                            'p-4 rounded-lg border',
                            rec.priority === 'high' ? 'bg-red-50 border-red-200' :
                            rec.priority === 'medium' ? 'bg-amber-50 border-amber-200' :
                            'bg-blue-50 border-blue-200'
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <span className={classNames(
                              'flex-shrink-0 text-xs px-2 py-1 rounded font-bold text-white',
                              rec.priority === 'high' ? 'bg-red-500' :
                              rec.priority === 'medium' ? 'bg-amber-500' :
                              'bg-blue-500'
                            )}>
                              {rec.priority === 'high' ? t('analysisDetail:coaching.recommendations.high') :
                               rec.priority === 'medium' ? t('analysisDetail:coaching.recommendations.med') :
                               t('analysisDetail:coaching.recommendations.low')}
                            </span>
                            <div>
                              <p className="text-sm font-medium text-gray-800">{title}</p>
                              <p className="text-xs text-gray-600 mt-1">{description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  {(!analysis.results.coaching.recommendations || analysis.results.coaching.recommendations.length === 0) && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700">{t('analysisDetail:coaching.recommendations.noRecommendations')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transcript Section */}
        {analysis.transcript && (
          <div className="bg-white shadow rounded-xl mb-6 overflow-hidden">
            <button
              onClick={() => setIsTranscriptExpanded(!isTranscriptExpanded)}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('analysisDetail:sections.callTranscript')}
                </h2>
              </div>
              <div className="flex items-center space-x-3">
                {analysis.audio.url && (
                  <a
                    href={analysis.audio.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100"
                  >
                    <PlayIcon className="h-4 w-4 mr-1" />
                    {t('analysisDetail:transcript.playAudio')}
                  </a>
                )}
                {isTranscriptExpanded ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {isTranscriptExpanded && (
              <div className="px-6 pb-6">
                {analysis.transcript.segments && analysis.transcript.segments.length > 0 ? (
                  <div className="space-y-4">
                    {analysis.transcript.segments.map((segment, idx) => {
                      // Use agent_speaker from backend to correctly identify who is the agent
                      const agentSpeaker = analysis.transcript?.agent_speaker || 'A';
                      const isAgent = segment.speaker === agentSpeaker;
                      const speakerLabel = isAgent ? t('analysisDetail:transcript.agent') :
                                          segment.speaker ? t('analysisDetail:transcript.customer') :
                                          t('analysisDetail:callInfo.unknown');
                      const avatarLetter = isAgent ? 'A' : segment.speaker ? 'C' : '?';

                      return (
                        <div key={idx} className="flex gap-3">
                          <div className={classNames(
                            'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
                            isAgent ? 'bg-indigo-100' : 'bg-amber-100'
                          )}>
                            <span className={classNames(
                              'text-xs font-bold',
                              isAgent ? 'text-indigo-600' : 'text-amber-600'
                            )}>
                              {avatarLetter}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={classNames(
                                'text-sm font-semibold',
                                isAgent ? 'text-indigo-600' : 'text-amber-600'
                              )}>
                                {speakerLabel}
                              </span>
                              {segment.start !== undefined && (
                                <span className="text-xs text-gray-400">
                                  {formatTimestamp(segment.start)}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                              {segment.text}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {analysis.transcript.text}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Call Information */}
        <div className="bg-white shadow rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('analysisDetail:sections.callInformation')}
            </h2>
          </div>
          <div className="p-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('analysisDetail:callInfo.agentId')}</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900">
                  {analysis.call.agent_id === 'unknown'
                    ? t('analysisDetail:callInfo.unknown')
                    : analysis.call.agent_id}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('analysisDetail:callInfo.contactId')}</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900">
                  {analysis.call.contact_id === 'unknown'
                    ? t('analysisDetail:callInfo.unknown')
                    : analysis.call.contact_id}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('analysisDetail:callInfo.duration')}</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900">
                  {formatDuration(analysis.call.duration_ms)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('analysisDetail:callInfo.language')}</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900">
                  {analysis.transcript?.language
                    ? t(`analysisDetail:callInfo.languages.${analysis.transcript.language}`, analysis.transcript.language)
                    : t('analysisDetail:callInfo.unknown')}
                </dd>
              </div>
              {analysis.metadata.completed_at && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('analysisDetail:callInfo.completedAt')}</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">
                    {new Date(analysis.metadata.completed_at).toLocaleString()}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default AnalysisDetail;
